import type { OptimizationModel } from '@/shared/generated'

import React, { memo, useMemo } from 'react'
import { prop } from 'lodash/fp'
import { Undo2Icon } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	getStatusBadgeVariant,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useOptimizationGroupQuery } from '@/entities/optimization-group'
import { isGlobalPricingCampaign } from '@/entities/pricing-campaign'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FormProvider } from '@/shared/components/HookForm'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import { Tag } from '@/shared/components/ui/tag'
import { useMutationObservers } from '@/shared/lib/useMutationObservers'
import { formatNumber } from '@/shared/tools/locale'

import { useOrderedPricingCampaignsProductsCounts } from '../lib/useOrderedPricingCampaignsProductsCounts'
import { usePricingCampaignOrdering } from '../lib/usePricingCampaignOrdering'
import { PricingCampaignsPriority } from './PricingCampaignsPriority'

export function PricingCampaignsPriorityModal({
	isOpen,
	close,
	optimizationGroupId,
	scenarioId,
	globalPcSetId,
}: {
	isOpen: boolean
	close: () => void
	optimizationGroupId?: string | null
	scenarioId?: string | null
	globalPcSetId?: string | null
}) {
	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='h-[calc(100vh-2rem)] max-w-5xl'>
				<PricingCampaignOrdering
					close={close}
					optimizationGroupId={optimizationGroupId}
					scenarioId={scenarioId}
					globalPcSetId={globalPcSetId}
				/>
			</ModalContent>
		</Modal>
	)
}
function PricingCampaignOrdering({
	close,
	optimizationGroupId,
	scenarioId,
	globalPcSetId,
}: {
	close: () => void
	optimizationGroupId?: string | null
	scenarioId?: string | null
	globalPcSetId?: string | null
}) {
	const { t } = useTranslation()

	useMutationObservers({
		mutationKey: ['pricing-campaign-reorder'],
		onSuccess: () => close(),
	})
	const optimizationGroupQuery = useOptimizationGroupQuery(
		optimizationGroupId!,
		{
			enabled: !!optimizationGroupId,
		}
	)
	const optimizationQuery = useOptimizationsQuery<OptimizationModel>({
		select: prop(optimizationGroupId!),
		enabled: !!optimizationGroupId,
	})
	const { methods, handleSubmit, isLoading, isProcessing, isError } =
		usePricingCampaignOrdering({
			optimizationGroupId,
			scenarioId,
			globalPcSetId,
		})

	return (
		<FormProvider
			className='-mx-4 flex flex-col gap-4 overflow-auto px-4'
			methods={methods}
			onSubmit={handleSubmit}
		>
			<ModalHeader>
				<ModalTitle>
					{t('pc.priority.modal.title', {
						defaultValue: 'Manage pricing campaigns priority',
					})}
				</ModalTitle>
			</ModalHeader>
			<ErrorBoundary>
				{optimizationGroupQuery.data && optimizationQuery.data && (
					<div className='rounded-lg border bg-secondary p-4'>
						<DescriptionList className='grid-cols-6'>
							<DescriptionGroup>
								<DescriptionTerm>{t('optimization_group')}</DescriptionTerm>
								<DescriptionDetail>
									{optimizationGroupQuery.data?.name}
								</DescriptionDetail>
							</DescriptionGroup>
							<DescriptionGroup>
								<DescriptionTerm>
									{t('optimization_groups_repricing_status')}
								</DescriptionTerm>
								<DescriptionDetail>
									<Tag
										size='sm'
										className='shrink-0'
										variant={getStatusBadgeVariant(
											optimizationQuery.data?.status
										)}
									>
										{t(`opt_${optimizationQuery.data?.status}`)}
									</Tag>
								</DescriptionDetail>
							</DescriptionGroup>
							<DescriptionGroup>
								<DescriptionTerm>
									{t('og.items', { defaultValue: 'Items in OG' })}
								</DescriptionTerm>
								<DescriptionDetail>
									{formatNumber(optimizationQuery.data?.total_products ?? 0)}
								</DescriptionDetail>
							</DescriptionGroup>
						</DescriptionList>
					</div>
				)}
				<ModalBody className='overflow-y-auto border-y pb-4'>
					{isError && (
						<Alert variant='destructive'>
							<AlertTitle>{t('fatal_error_title')}🐞</AlertTitle>
							<AlertDescription>{t('fatal_error_desc')}</AlertDescription>
						</Alert>
					)}
					{isLoading || isProcessing ? (
						<LoadingSkeleton />
					) : (
						<PricingCampaignsOrderingSections
							scenarioId={scenarioId}
							optimizationGroupId={optimizationGroupId}
						/>
					)}
				</ModalBody>
			</ErrorBoundary>
			<ModalFooter>
				<ModalClose asChild>
					<Button
						className='mr-auto'
						data-testid='pcs-priority-modal-cancel'
						disabled={isProcessing}
					>
						{t('general_cancel')}
					</Button>
				</ModalClose>
				{methods.formState.isDirty && (
					<Button
						data-testid='pcs-priority-modal-rever'
						variant='secondary'
						onClick={() => methods.reset()}
						disabled={isProcessing}
						className='ml-auto'
					>
						<Undo2Icon />
						{t('app.revert_changes')}
					</Button>
				)}
				<Button
					data-testid='pcs-priority-modal-save'
					variant='primary'
					disabled={!methods.formState.isDirty || isProcessing}
					onClick={handleSubmit}
				>
					{isProcessing && <Spinner />}
					{t('pc.save_priority_changes', {
						defaultValue: 'Save priority changes',
					})}
				</Button>
			</ModalFooter>
		</FormProvider>
	)
}
function PricingCampaignsOrderingSections({
	optimizationGroupId,
	scenarioId,
}: {
	optimizationGroupId?: string | null
	scenarioId?: string | null
}) {
	const { t } = useTranslation()

	const { control, watch } = useFormContext()
	const isItemDragDisabled = useMemo(
		() => (optimizationGroupId ? isGlobalPricingCampaign : undefined),
		[optimizationGroupId]
	)
	const predicts = useOrderedPricingCampaignsProductsCounts(
		[...watch('global'), ...watch('local')],
		scenarioId
	)

	return (
		<>
			<Controller
				name='global'
				control={control}
				render={({ field: { value, onChange } }) => {
					if (!value?.length) {
						return <div />
					}

					return (
						<>
							<div className='sticky top-0 z-50 bg-background py-2'>
								<h4 className='text-sm text-muted-foreground uppercase'>
									{t('app.global_pricing_campaigns', {
										defaultValue: 'Global pricing campaigns',
									})}
								</h4>
							</div>
							<div className='rounded-xl border bg-muted/30 p-2'>
								<PricingCampaignsPriority
									predicts={predicts}
									pricingCampaigns={value}
									onChange={onChange}
									isItemDragDisabled={isItemDragDisabled}
								/>
							</div>
						</>
					)
				}}
			/>
			<Controller
				name='local'
				control={control}
				render={({ field: { value, onChange } }) => {
					if (!value?.length) {
						return <div />
					}

					return (
						<>
							<div className='sticky top-0 z-50 bg-background py-2'>
								<h4 className='text-sm text-muted-foreground uppercase'>
									{t('app.local_pricing_campaigns', {
										defaultValue: 'Local pricing campaigns',
									})}
								</h4>
							</div>
							<div className='rounded-xl border bg-muted/30 p-2'>
								<PricingCampaignsPriority
									predicts={predicts}
									pricingCampaigns={value}
									onChange={onChange}
									isItemDragDisabled={isItemDragDisabled}
								/>
							</div>
						</>
					)
				}}
			/>
		</>
	)
}

const LoadingSkeleton = memo(() => (
	<div className='w-full'>
		<div className='relative space-y-1'>
			{[...Array(10)].map((_, index) => (
				<div
					key={index}
					className='flex h-14 w-full items-center space-x-2 rounded-sm bg-secondary px-4'
				>
					<Skeleton className='size-4' />
					<Skeleton className='size-7' />
					<div className='space-y-2'>
						<Skeleton className='h-5 w-52' />
						<Skeleton className='h-3 w-72' />
					</div>
				</div>
			))}
		</div>
	</div>
))
